import {createSlice} from '@reduxjs/toolkit'
import type {PayloadAction} from '@reduxjs/toolkit'
import {resetState} from '../actions'

export interface UserWorkspace {
    id: string
    name: string
    workspaceUrlId: string
    workspaceTaskPrefixId: string
    avatar: string | null
    role: 'owner' | 'member' | 'guest'
}

export type LoginSection = 'my-tasks' | 'team' | 'projects' | 'all-tasks'

export interface NotificationSettings {
    emailEnabled: boolean
    assignments: boolean
    comments: boolean
    mentions: boolean
    weeklyDigest: boolean
    productUpdates: boolean
}

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
    emailEnabled: true,
    assignments: true,
    comments: true,
    mentions: true,
    weeklyDigest: true,
    productUpdates: true,
}

export interface AuthState {
    id: string
    email: string
    fullName: string
    color: string
    avatarImage: string
    username: string
    workspaces: UserWorkspace[]
    workspacesLoading: boolean
    timezone: string
    loginSection: LoginSection
    lastWorkspaceUrlId: string | null
    notificationSettings: NotificationSettings
}

const initialState: AuthState | null = null

const authSlice = createSlice({
    name: 'auth',
    initialState: initialState as AuthState | null,
    reducers: {
        initAuthSaga: (_state) => {},
        signInSaga: (_state, _action: PayloadAction<{email: string, password: string}>) => {},
        signInGoogleSaga: (_state) => {},
        resetPasswordSaga: (_state, _action: PayloadAction<string>) => {},
        logoutSaga: (_state) => {},
        verifyEmailSaga: (_state, _action: PayloadAction<string>) => {},
        resendVerificationSaga: (_state) => {},
        updateFullNameSaga: (_state, _action: PayloadAction<string>) => {},
        setFullName: (state, action: PayloadAction<string>) => {
            if (state) state.fullName = action.payload
        },
        updateUserAvatarSaga: (_state, _action: PayloadAction<string | null>) => {},
        setAvatarUrl: (state, action: PayloadAction<string | null>) => {
            if (state) state.avatarImage = action.payload ?? ''
        },
        updateUsernameSaga: (_state, _action: PayloadAction<string>) => {},
        setUsername: (state, action: PayloadAction<string>) => {
            if (state) state.username = action.payload
        },
        updateEmailSaga: (_state, _action: PayloadAction<string>) => {},
        setEmail: (state, action: PayloadAction<string>) => {
            if (state) state.email = action.payload
        },
        sendEmailChangeSaga: (_state, _action: PayloadAction<{ newEmail: string; onSuccess?: () => void; onError?: (msg: string) => void }>) => {},
        verifyEmailChangeSaga: (_state, _action: PayloadAction<{ code: string; onSuccess?: () => void; onError?: (msg: string) => void }>) => {},
        initiateDeleteAccountSaga: () => {},
        deleteAccountSaga: (_state, _action: PayloadAction<{ onError?: (msg: string) => void }>) => {},
        setTimezone: (state, action: PayloadAction<string>) => {
            if (state) state.timezone = action.payload
        },
        updateLoginSectionSaga: (_state, _action: PayloadAction<LoginSection>) => {},
        setLoginSection: (state, action: PayloadAction<LoginSection>) => {
            if (state) state.loginSection = action.payload
        },
        updateLastWorkspaceSaga: (_state, _action: PayloadAction<string>) => {},
        setLastWorkspaceUrlId: (state, action: PayloadAction<string>) => {
            if (state) state.lastWorkspaceUrlId = action.payload
        },
        loadNotificationSettingsSaga: (_state) => {},
        updateNotificationSettingsSaga: (_state, _action: PayloadAction<Partial<NotificationSettings>>) => {},
        setNotificationSettings: (state, action: PayloadAction<NotificationSettings>) => {
            if (state) state.notificationSettings = action.payload
        },
        setAuth: (_state, action: PayloadAction<AuthState>) => action.payload,
        resetAuth: (_state) => null,
        setWorkspaces: (state, action: PayloadAction<UserWorkspace[]>) => {
            if (state) {
                state.workspaces = action.payload
            }
        },
        setWorkspacesLoading: (state, action: PayloadAction<boolean>) => {
            if (state) {
                state.workspacesLoading = action.payload
            }
        },
        clearWorkspaces: (state) => {
            if (state) {
                state.workspaces = []
                state.workspacesLoading = true
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(resetState, () => null)
    }
})

export const {
    initAuthSaga,
    signInSaga,
    signInGoogleSaga,
    resetPasswordSaga,
    logoutSaga,
    verifyEmailSaga,
    resendVerificationSaga,
    updateFullNameSaga,
    setFullName,
    updateUserAvatarSaga,
    setAvatarUrl,
    updateUsernameSaga,
    setUsername,
    updateEmailSaga,
    setEmail,
    sendEmailChangeSaga,
    verifyEmailChangeSaga,
    initiateDeleteAccountSaga,
    deleteAccountSaga,
    setTimezone,
    updateLoginSectionSaga,
    setLoginSection,
    updateLastWorkspaceSaga,
    setLastWorkspaceUrlId,
    loadNotificationSettingsSaga,
    updateNotificationSettingsSaga,
    setNotificationSettings,
    setAuth,
    resetAuth,
    setWorkspaces,
    setWorkspacesLoading,
    clearWorkspaces,
} = authSlice.actions
export default authSlice.reducer
